/**
 * WHAT THE OFFICE WROTE FOR HER ABOUT A CLEAN DONE, drawn alike wherever a clean's report is: Home's
 * last clean, Messages' "Your home is ready", and one visit's report (`screens/VisitReport.tsx`,
 * home #21), each from the journal ops pushes for the clean (ops #98).
 *
 * - THE NOTE is the office's words for her, never the cleaner's own (home #18, ops #98 phase 2), so
 *   it is drawn as ops sent it, with no quotation marks, and never with "From" her under it, as the
 *   artboards drew the cleaner's own words.
 * - "CLEANED BY" the cleaner's first name is under it, the same with a note or without, with no gap
 *   above it where there is no note (the fifth client-journey walk).
 * - EACH THING NOTICED on the clean, the office's line for her in ops's order (home #19, ops #98
 *   phase 3), as "10. Your home is ready, on Messages" draws "Noticed: a dripping tap in the guest
 *   bath." (`noticedLine`): the small muted ink, as "Cleaned by" is, and that artboard's 10px under
 *   what is above it, none above the first where nothing is. Never credited to the cleaner.
 * - TWO SIZES. Home's card draws the note in body type with 8px under it; Messages and the report
 *   draw it small with 4px, as "10. Your home is ready, on Messages" does (`small`).
 *
 * Nothing at all where the office wrote nothing and ops named no cleaner (`reportSays`), so a card
 * keeps no gap for it.
 */
import type { Journal } from '../api.ts';
import { cleanedBy, noticedLine } from '../words.ts';

interface ReportProps {
  /** What the office wrote for her about the clean, or null where it has written nothing yet. */
  readonly journal: Journal | null;
  /** The cleaner's first name, as ops sent it, or none. */
  readonly cleaner: string | undefined;
  /** The note in small type, as Messages and the report draw it, rather than Home's body type. */
  readonly small?: boolean;
}

/** Whether the report says anything: a note, who cleaned it, or a thing noticed. */
export const reportSays = ({ journal, cleaner }: Omit<ReportProps, 'small'>): boolean =>
  journal?.whatWasDone !== undefined || cleaner !== undefined || (journal?.noticed.length ?? 0) > 0;

export function Report({ journal, cleaner, small = false }: ReportProps) {
  const note = journal?.whatWasDone;
  const noticed = journal?.noticed ?? [];
  const above = note !== undefined || cleaner !== undefined;
  return (
    <>
      {note === undefined ? null : (
        // The office's words for her, as ops sent them, with no quotation marks.
        <p className={`${small ? 'text-small' : 'text-body'} text-pretty text-text-body`}>{note}</p>
      )}
      {cleaner === undefined ? null : (
        // Who cleaned it, the same with a note or without: under a note the artboard's gap, and with
        // none nothing above it to keep a gap for.
        <p
          className={`${note === undefined ? '' : small ? 'mt-1 ' : 'mt-2 '}text-small text-text-muted`}
        >
          {cleanedBy(cleaner)}
        </p>
      )}
      {noticed.map(({ summary }, index) => (
        // The office's line, as ops sent it, in ops's order; never credited to the cleaner.
        <p
          key={index}
          className={`${above || index > 0 ? 'mt-2.5 ' : ''}text-small text-pretty text-text-muted`}
        >
          {noticedLine(summary)}
        </p>
      ))}
    </>
  );
}
